import React, { useEffect, useState, useRef } from "react";
import Grid from "@material-ui/core/Grid";
import { Theme } from "@material-ui/core/styles/createMuiTheme";
import { withStyles } from "@material-ui/core/styles";
import createStyles from "@material-ui/core/styles/createStyles";
import IconButton from "@material-ui/core/IconButton";
import FirstPageIcon from "@material-ui/icons/FirstPage";
import LastPageIcon from "@material-ui/icons/LastPage";
import NextIcon from "@material-ui/icons/NavigateNext";
import PrevIcon from "@material-ui/icons/NavigateBefore";
import MenuItem from "@material-ui/core/MenuItem";
import Menu from "@material-ui/core/Menu";
import Typography from "@material-ui/core/Typography";
import UiButton from "./UiButton";
import { tableTheme } from "./tableTheme";

const styles = (theme: Theme) =>
  createStyles({
    numberItems: {
      paddingLeft: theme.spacing.unit * 2,
    },
    footerModern: {
      borderTop: `1px solid ${tableTheme.border}`,
      backgroundColor: tableTheme.surface,
      padding: "10px 24px",
      flexShrink: 0,
    },
    countModern: {
      fontSize: "0.875rem",
      color: tableTheme.textPrimary,
    },
    selectedBadge: {
      marginLeft: 8,
      color: tableTheme.secondaryDark,
      fontWeight: 500,
    },
    pageSizeButtonModern: {
      color: tableTheme.textPrimary,
      padding: "2px 8px",
      minWidth: 42,
      minHeight: 28,
      border: `1px solid ${tableTheme.border}`,
      borderRadius: tableTheme.radiusSm,
      fontSize: "0.75rem",
      backgroundColor: tableTheme.surface,
    },
    pageInputModern: {
      maxWidth: 36,
      height: 28,
      padding: "2px 6px",
      border: `1px solid ${tableTheme.border}`,
      borderRadius: tableTheme.radiusXs,
      fontSize: "0.75rem",
      textAlign: "center" as const,
    },
    navButtonModern: {
      padding: 4,
      width: 28,
      height: 28,
      backgroundColor: "#eceef2",
      color: "#949bab",
    },
    navIconModern: {
      fontSize: 18,
      color: "inherit",
    },
    navPagerGroup: {
      display: "flex",
      alignItems: "center",
      gap: "8px",
    },
    pageLabelModern: {
      fontSize: "0.875rem",
      color: tableTheme.textSecondary,
      marginRight: 4,
    },
    pageSizeGroup: {
      display: "flex",
      alignItems: "center",
      flexWrap: "nowrap",
      whiteSpace: "nowrap" as const,
      gap: 8,
    },
    navControlsModern: {
      display: "flex",
      alignItems: "center",
      flexWrap: "wrap",
      justifyContent: "flex-end",
      gap: "12px 20px",
    },
  });

const TableNavigation = (props: any) => {
  const {
    rowsPerPageOptions,
    rowsPerPage,
    onChangePerPage,
    pagination: { number, totalPages, first, last, totalElements },
    itemName,
    numberSelected,
    onChangePage,
    classes,
    variant = "default",
  } = props;
  const isModern = variant === "modern";
  const inputRef = useRef(null);

  const [pageDirectNo, setPageDirectNo] = useState(number + 1);
  const [anchorEl, setAnchorEl] = useState<any>(null);

  const closeMenu = () => setAnchorEl(null);

  const handleClick = (event: any) => {
    setAnchorEl(event.currentTarget);
  };

  const goToFirst = () => onChangePage(0);

  const goToPrevious = () => onChangePage(number - 1);

  const goToNext = () => onChangePage(number + 1);

  const goToLast = () => {
    onChangePage(totalPages - 1);
  };

  const handleChange = (e: any) =>
    setPageDirectNo(
      Number(e.target.value) < totalPages + 1
        ? Number(e.target.value)
        : Number(totalPages),
    );

  const handleSubmit = (e: any) => {
    e.preventDefault();
    onChangePage(pageDirectNo - 1);
  };

  useEffect(() => setPageDirectNo(number + 1), [number]);

  useEffect(() => {
    if (inputRef !== null) {
      //@ts-ignore
      inputRef.current.addEventListener("focus", () => {
        //@ts-ignore
        inputRef.current.select();
      });
    }
  }, [inputRef]);

  const countBlock = (
    <>
      {totalElements} {itemName}
      {numberSelected > 0 && (
        <span className={isModern ? classes.selectedBadge : undefined}>
          {isModern
            ? ` · ${numberSelected} sélectionné${numberSelected > 1 ? "s" : ""}`
            : `, ${numberSelected} sélectionné${numberSelected > 1 ? "s" : ""}`}
        </span>
      )}
    </>
  );

  return (
    <Grid
      item
      style={{ flexGrow: isModern ? 0 : 1, flexShrink: 0, width: "100%" }}
      className={isModern ? classes.footerModern : undefined}
    >
      <Grid
        container
        direction="row"
        justify="space-between"
        alignItems="center"
      >
        <Grid
          item
          className={isModern ? classes.countModern : classes.numberItems}
        >
          {isModern ? (
            <Typography component="span" className={classes.countModern}>
              {countBlock}
            </Typography>
          ) : (
            countBlock
          )}
        </Grid>

        <Grid item>
          {isModern ? (
            <div className={classes.navControlsModern}>
              {rowsPerPageOptions?.length > 0 && (
                <div className={classes.pageSizeGroup}>
                  <Typography
                    component="span"
                    className={classes.pageLabelModern}
                  >
                    Par page :
                  </Typography>
                  <UiButton
                    aria-owns={anchorEl ? "simple-menu" : undefined}
                    aria-haspopup="true"
                    className={classes.pageSizeButtonModern}
                    onClick={handleClick}
                  >
                    {rowsPerPage}
                  </UiButton>
                  <Menu
                    id="per-page"
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={closeMenu}
                  >
                    {rowsPerPageOptions.map((item: any, index: any) => (
                      <MenuItem
                        key={index}
                        onClick={() => {
                          onChangePerPage(item);
                          closeMenu();
                        }}
                      >
                        {item}
                      </MenuItem>
                    ))}
                  </Menu>
                </div>
              )}
              <Typography component="span" className={classes.pageLabelModern}>
                Page {number + 1} - {totalPages}
              </Typography>
              <div className={classes.navPagerGroup}>
                <IconButton
                  disabled={first}
                  onClick={goToFirst}
                  title="Première page"
                  className={classes.navButtonModern}
                >
                  <FirstPageIcon
                    fontSize="inherit"
                    className={classes.navIconModern}
                  />
                </IconButton>
                <IconButton
                  disabled={first}
                  onClick={goToPrevious}
                  title="Page précédente"
                  className={classes.navButtonModern}
                >
                  <PrevIcon
                    fontSize="inherit"
                    className={classes.navIconModern}
                  />
                </IconButton>
                <form onSubmit={handleSubmit}>
                  <input
                    onChange={handleChange}
                    value={pageDirectNo}
                    className={classes.pageInputModern}
                    ref={inputRef}
                  />
                  <input type="submit" style={{ display: "none" }} />
                </form>
                <IconButton
                  disabled={last}
                  onClick={goToNext}
                  title="Page suivante"
                  className={classes.navButtonModern}
                >
                  <NextIcon
                    fontSize="inherit"
                    className={classes.navIconModern}
                  />
                </IconButton>
                <IconButton
                  disabled={last}
                  onClick={goToLast}
                  title="Dernière page"
                  className={classes.navButtonModern}
                >
                  <LastPageIcon
                    fontSize="inherit"
                    className={classes.navIconModern}
                  />
                </IconButton>
              </div>
            </div>
          ) : (
            <Grid
              container
              direction="row"
              justify="flex-end"
              alignItems="center"
              spacing={8}
            >
              {rowsPerPageOptions?.length > 0 && (
                <Grid
                  item
                  className={classes.pageSizeGroup}
                  style={{ marginRight: "24px" }}
                >
                  <Typography component="span">Par page :</Typography>
                  <UiButton
                    aria-owns={anchorEl ? "simple-menu" : undefined}
                    aria-haspopup="true"
                    style={{
                      color: "black",
                      padding: "0",
                      minWidth: "50px",
                      border: "1px solid black",
                      marginLeft: "4px",
                    }}
                    onClick={handleClick}
                  >
                    {rowsPerPage}
                  </UiButton>
                  <Menu
                    id="per-page"
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={closeMenu}
                  >
                    {rowsPerPageOptions.map((item: any, index: any) => (
                      <MenuItem
                        key={index}
                        onClick={() => {
                          onChangePerPage(item);
                          closeMenu();
                        }}
                      >
                        {item}
                      </MenuItem>
                    ))}
                  </Menu>
                </Grid>
              )}
              <Grid item>
                <Typography component="span">
                  Page {number + 1} - {totalPages}
                </Typography>
              </Grid>
              <Grid item>
                <IconButton
                  disabled={first}
                  onClick={goToFirst}
                  title="Première page"
                >
                  <FirstPageIcon fontSize="small" />
                </IconButton>
              </Grid>
              <Grid item>
                <IconButton
                  disabled={first}
                  onClick={goToPrevious}
                  title="Page précédente"
                >
                  <PrevIcon fontSize="small" />
                </IconButton>
              </Grid>
              <Grid item>
                <form onSubmit={handleSubmit}>
                  <input
                    onChange={handleChange}
                    value={pageDirectNo}
                    style={{ maxWidth: 35 }}
                    ref={inputRef}
                  />
                  <input type="submit" style={{ display: "none" }} />
                </form>
              </Grid>
              <Grid item>
                <IconButton
                  disabled={last}
                  onClick={goToNext}
                  title="Page suivante"
                >
                  <NextIcon fontSize="small" />
                </IconButton>
              </Grid>
              <Grid item>
                <IconButton
                  disabled={last}
                  onClick={goToLast}
                  title="Dernière page"
                >
                  <LastPageIcon fontSize="small" />
                </IconButton>
              </Grid>
            </Grid>
          )}
        </Grid>
      </Grid>
    </Grid>
  );
};

TableNavigation.defaultProps = {
  numberSelected: 0,
  variant: "default" as const,
};

export default withStyles(styles)(TableNavigation);
